// ---------------------------------------------------------------------------
// Print jobs.
//
// Queueing and retrying are open to anybody signed in: the cashier who
// completed the sale owes the customer a receipt, and is the person standing
// in front of a printer that did not produce one. The job LIST is management.
//
// A print request names a printer ROLE, never an address. The frontend cannot
// choose where the backend dials - that comes from the saved configuration,
// which only an owner or manager can set.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type { RasterPayload } from '../utils/rasterise';

export type PrintJobStatus = 'QUEUED' | 'PRINTING' | 'SENT' | 'FAILED' | 'CANCELLED';
export type PrintJobOrigin = 'ORIGINAL' | 'REPRINT' | 'TEST' | 'HELD_ORDER';
export type PrintDocumentType =
  | 'CASHIER_RECEIPT'
  | 'KITCHEN_TICKET'
  | 'TEST_RECEIPT'
  | 'TEST_KITCHEN_TICKET'
  | 'HELD_ORDER_SHEET'
  | 'SHIFT_REPORT'
  | 'TEST_SHIFT_REPORT';

export interface PrintJob {
  id: string;
  printerRole: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  /// Which printer this paper is for. Sent whenever the caller knows, which
  /// since printers became a list is every real reprint - a role is a
  /// description of a kind of paper, not the address of a device.
  printerProfileId?: string | null;
  documentType: PrintDocumentType;
  saleId: string | null;
  orderId: string | null;
  kitchenTicketId: string | null;
  status: PrintJobStatus;
  origin: PrintJobOrigin;
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  isReprint: boolean;
  queuedAt: string;
  startedAt: string | null;
  sentAt: string | null;
  failedAt: string | null;
  requestedBy: { id: string; fullName: string } | null;
}

export interface QueuePrintInput {
  printerRole: 'CASHIER_RECEIPT' | 'KITCHEN_TICKET';
  /// Which printer this paper is for. Sent whenever the caller knows, which
  /// since printers became a list is every real reprint - a role describes a
  /// KIND of paper, not the address of a device.
  printerProfileId?: string | null;
  documentType: PrintDocumentType;
  raster: RasterPayload;
  saleId?: string | null;
  orderId?: string | null;
  kitchenTicketId?: string | null;
  heldOrderId?: string | null;
  shiftReportId?: string | null;
  receiptDesignVersionId?: string | null;
  kitchenDesignVersionId?: string | null;
  shiftReportDesignVersionId?: string | null;
  origin?: PrintJobOrigin;
  unique?: string;
  isReprint?: boolean;
  immediate?: boolean;
}

export interface QueuePrintResult {
  job: { id: string; status: PrintJobStatus; documentType: PrintDocumentType };
  created: boolean;
  result: { status: PrintJobStatus; error?: string; bytesSent?: number } | null;
  /// Present only when this API leaves printing to the shop's print agent AND
  /// the job is still QUEUED - which is to say, when the paper now depends on
  /// a process in the shop rather than on anything the server can do. Absent
  /// on a till that prints for itself.
  awaitingAgent?: { seenRecently: boolean; lastSeenAt: string | null };
}

/// What became of one job - followed by a copy the till just sent, until the
/// shop's print agent has printed it or could not.
export function fetchPrintJobStatus(id: string) {
  return api.get<{ id: string; status: PrintJobStatus; attempts: number; error: string | null }>(
    `/print-jobs/${id}/status`,
  );
}

export function useQueuePrintJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: QueuePrintInput) => api.post<QueuePrintResult>('/print-jobs', body),
    retry: false,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['print-jobs'] }),
  });
}

/// What the till shows after a sale: did the receipt go out?
export function useJobsForSale(saleId: string | null) {
  return useQuery({
    queryKey: ['print-jobs', 'sale', saleId],
    queryFn: () => api.get<{ jobs: PrintJob[] }>(`/print-jobs/for-sale/${saleId}`),
    enabled: Boolean(saleId),
    // A queued job becomes sent or failed within seconds; the till should see
    // that without anybody reloading.
    refetchInterval: (query) =>
      (query.state.data?.jobs ?? []).some((job) => job.status === 'QUEUED' || job.status === 'PRINTING')
        ? 3000
        : false,
  });
}

export function usePrintJobs(filters: { status?: string; take?: number } = {}, enabled = true) {
  const search = new URLSearchParams();
  if (filters.status) search.set('status', filters.status);
  if (filters.take) search.set('take', String(filters.take));

  return useQuery({
    queryKey: ['print-jobs', filters],
    queryFn: () => api.get<{ jobs: PrintJob[] }>(`/print-jobs?${search.toString()}`),
    enabled,
    staleTime: 10_000,
  });
}

/// Reuses the SAME job. Pressing Retry three times does not owe three receipts.
export function useRetryPrintJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) =>
      api.post<{ result: { status: PrintJobStatus; error?: string } }>(
        `/print-jobs/${jobId}/retry`,
        {},
      ),
    retry: false,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['print-jobs'] }),
  });
}

export function useCancelPrintJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => api.post<{ job: PrintJob }>(`/print-jobs/${jobId}/cancel`, {}),
    retry: false,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['print-jobs'] }),
  });
}
